import api from './api';

/**
 * Service pour la gestion de la validation des listings par les administrateurs
 */
class ListingValidationService {
    
    /**
     * Récupérer les annonces en attente de validation
     * @param {Object} params - Paramètres de recherche
     * @param {number} params.page - Page à récupérer (par défaut: 1)
     * @param {number} params.limit - Nombre d'éléments par page (par défaut: 10)
     * @param {string} params.category - ID de catégorie pour filtrer
     * @param {string} params.city - Ville pour filtrer
     * @returns {Promise<Object>} Réponse avec les listings en attente
     */
    async getPendingListings(params = {}) {
        const { page = 1, limit = 10, category, city } = params;
        
        const queryParams = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString()
        });
        
        if (category) queryParams.append('category', category);
        if (city) queryParams.append('city', city);
        
        console.log('[ListingValidationService] Fetching pending listings:', { page, limit, category, city });
        
        try {
            const response = await api.get(`/admin/listings/pending?${queryParams}`);
            console.log('[ListingValidationService] Pending listings fetched:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ListingValidationService] Error fetching pending listings:', error);
            throw error;
        }
    }
    
    /**
     * Valider une annonce
     * @param {string} listingId - ID de l'annonce à valider
     * @param {Object} data - Données de validation
     * @param {string} data.notes - Notes internes optionnelles
     * @returns {Promise<Object>} Réponse de validation
     */
    async validateListing(listingId, data = {}) {
        console.log('[ListingValidationService] Validating listing:', listingId, data);
        
        try {
            const response = await api.post(`/admin/listings/${listingId}/validate`, data);
            console.log('[ListingValidationService] Listing validated:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ListingValidationService] Error validating listing:', error);
            throw error;
        }
    }
    
    /**
     * Rejeter une annonce
     * @param {string} listingId - ID de l'annonce à rejeter
     * @param {Object} data - Données de rejet
     * @param {string} data.reason - Motif du rejet (obligatoire)
     * @param {string} data.notes - Notes internes optionnelles
     * @returns {Promise<Object>} Réponse de rejet
     */
    async rejectListing(listingId, data) {
        if (!data.reason || typeof data.reason !== 'string') {
            throw new Error('Le motif de rejet est obligatoire');
        }
        
        if (data.reason.trim().length < 10) {
            throw new Error('Le motif de rejet doit contenir au moins 10 caractères');
        }
        
        console.log('[ListingValidationService] Rejecting listing:', listingId, data);
        
        try {
            const response = await api.post(`/admin/listings/${listingId}/reject`, data);
            console.log('[ListingValidationService] Listing rejected:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ListingValidationService] Error rejecting listing:', error);
            throw error;
        }
    }
    
    /**
     * Changer manuellement le statut d'une annonce (admin uniquement)
     * @param {string} listingId - ID de l'annonce
     * @param {Object} data - Données de changement de statut
     * @param {string} data.status - Nouveau statut
     * @param {string} data.reason - Motif du changement
     * @returns {Promise<Object>} Réponse de changement de statut
     */
    async changeListingStatus(listingId, data) {
        console.log('[ListingValidationService] Changing listing status:', listingId, data);
        
        try {
            const response = await api.put(`/admin/listings/${listingId}/status`, data);
            console.log('[ListingValidationService] Listing status changed:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ListingValidationService] Error changing listing status:', error);
            throw error;
        }
    }
    
    /**
     * Récupérer l'historique des actions sur les annonces avec filtres avancés
     * @param {Object} params - Paramètres de recherche
     * @param {number} params.page - Page à récupérer (par défaut: 1)
     * @param {number} params.limit - Nombre d'éléments par page (par défaut: 50)
     * @param {string} params.listingId - ID d'annonce spécifique
     * @param {string} params.adminFilter - ID admin pour filtrer
     * @param {string} params.action - Type d'action à filtrer
     * @param {string} params.status - Statut à filtrer
     * @param {string} params.dateFrom - Date de début
     * @param {string} params.dateTo - Date de fin
     * @param {string} params.orderBy - Champ de tri
     * @param {string} params.order - Ordre de tri (asc/desc)
     * @returns {Promise<Object>} Historique des actions
     */
    async getListingHistory(params = {}) {
        const { 
            page = 1, 
            limit = 50, 
            listingId, 
            adminFilter, 
            action, 
            status, 
            dateFrom, 
            dateTo, 
            orderBy, 
            order 
        } = params;
        
        const queryParams = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString()
        });
        
        if (listingId) queryParams.append('listingId', listingId);
        if (adminFilter) queryParams.append('adminFilter', adminFilter);
        if (action) queryParams.append('action', action);
        if (status) queryParams.append('status', status);
        if (dateFrom) queryParams.append('dateFrom', dateFrom);
        if (dateTo) queryParams.append('dateTo', dateTo);
        if (orderBy) queryParams.append('orderBy', orderBy);
        if (order) queryParams.append('order', order);
        
        console.log('[ListingValidationService] Fetching listing history:', params);
        
        try {
            const response = await api.get(`/admin/listings/history?${queryParams}`);
            console.log('[ListingValidationService] Listing history fetched:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ListingValidationService] Error fetching listing history:', error);
            throw error;
        }
    }

    /**
     * Récupérer les statistiques détaillées de l'historique
     * @param {Object} params - Paramètres pour les statistiques
     * @param {string} params.period - Période en jours (7, 30, 90, 365)
     * @param {string} params.groupBy - Groupement temporel (hour, day, week, month)
     * @returns {Promise<Object>} Statistiques détaillées
     */
    async getHistoryStats(params = {}) {
        const { period = '30', groupBy = 'day' } = params;
        
        const queryParams = new URLSearchParams({
            period: period.toString(),
            groupBy
        });
        
        console.log('[ListingValidationService] Fetching history stats:', params);
        
        try {
            const response = await api.get(`/admin/listings/history/stats?${queryParams}`);
            console.log('[ListingValidationService] History stats fetched:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ListingValidationService] Error fetching history stats:', error);
            throw error;
        }
    }
    
    /**
     * Récupérer les statistiques de validation
     * @returns {Promise<Object>} Statistiques de validation
     */
    async getValidationStats() {
        console.log('[ListingValidationService] Fetching validation stats');
        
        try {
            const response = await api.get('/admin/listings/stats');
            console.log('[ListingValidationService] Validation stats fetched:', response.data);
            return response.data;
        } catch (error) {
            console.error('[ListingValidationService] Error fetching validation stats:', error);
            throw error;
        }
    }
}

// Instance unique du service
const listingValidationService = new ListingValidationService();

export default listingValidationService;
